import styled from '@emotion/styled';
import { token } from '@atlaskit/tokens';
import { CustomDynamicResultTable } from '@css-xp/base';

const commonColumnStyle = `
  width: 140px !important;
  border-left: 1px solid ${token('color.background.accent.gray.subtlest')} !important;
`;

const boxShadowBeforeAddNewButtonStyle = `
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  right: auto;
  background: ${token('color.background.accent.gray.subtlest')};
  opacity: 0.5;
  width: 0;
  height: 100%;
`;

const boxShadowAfterAddNewButtonStyle = `
  content: '';
  position: absolute;
  left: auto;
  top: 0;
  right: 0;
  background: ${token('color.background.accent.gray.subtlest')};
  opacity: 0.5;
  width: 0;
  height: 100%;
`;

export const CartLineItemsTableContainer = styled(CustomDynamicResultTable)`
  .custom-table-container {
    overflow: auto;

    > table {
      border-collapse: collapse;
      border-spacing: 0;
      > tbody > tr,
      > thead > tr {
        border-left: 1px solid ${token('color.background.accent.gray.subtlest')} !important;
        border-right: 1px solid ${token('color.background.accent.gray.subtlest')} !important;
      }
      > thead > tr {
        background: ${token('elevation.surface.sunken')};
        border-top: 1px solid ${token('color.background.accent.gray.subtlest')} !important;
      }

      > thead {
        th {
          :first-child {
            width: 24px;
            padding: ${token('space.025')} !important;
          }
          background: ${token('elevation.surface.sunken')} !important;
          height: ${token('space.200')} !important;
          align-items: center;
          color: ${token('color.text.subtle')}!important;
          font-weight: ${token('font.weight.bold')}!important; // this is required to override table dynamic table style
          font-size: 12px !important;
          :nth-last-child(1) {
            position: sticky;
            right: -1px !important;
            width: 32px;
            &:before {
              ${boxShadowBeforeAddNewButtonStyle}
            }
            &:after {
              ${boxShadowAfterAddNewButtonStyle}
            }
          }
        }
      }
    }
  }

  .product-rows-container {
    &--product-wrapper {
      &--product-content {
        padding-left: ${token('space.150')};
      }
    }
    &--product-name {
      font-size: 14px;
      font-weight: ${token('font.weight.regular')};
      color: ${token('color.text')};
    }
    &--entitlement-number {
      color: ${token('color.text.subtle')};
    }
  }

  .col-cloud-site-name {
    display: flex;
    align-items: center;
    gap: ${token('space.100')};
    padding-left: ${token('space.500')} !important;
    > div {
      max-width: 280px;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }
  }

  .cloud-site-empty-cell {
    border-left: 1px solid ${token('elevation.surface')} !important;
  }

  .col-product-name {
    width: 380px;
  }

  .col-product-list-price,
  .col-product-partner-discount,
  .col-product-tax-estimate,
  .col-product-total {
    div,
    span {
      display: flex !important;
      justify-content: flex-end;
    }
  }

  .col-product-other-discount {
    div {
      margin-right: 0;
      width: 100%;
    }
  }

  .col-common {
    ${commonColumnStyle}
  }

  .col-common-header {
    ${commonColumnStyle}
    span {
      color: ${token('color.text.subtle')};
      font-weight: ${token('font.weight.bold')};
      font-size: 12px;
    }
  }

  .col-product-add-new {
    width: 32px;
    position: sticky !important;
    right: -1px !important;
    background: ${token('elevation.surface')} !important;
    border-bottom: 1px solid ${token('color.background.accent.gray.subtlest')};
    border-left: 1px solid ${token('color.background.accent.gray.subtlest')};

    .add-new-product-button {
      span[aria-label='add-product'] {
        color: ${token('color.text.subtle')};
      }
    }
    &:before {
      ${boxShadowBeforeAddNewButtonStyle}
    }
    &:after {
      ${boxShadowAfterAddNewButtonStyle}
    }
  }

  .cloud-site-row {
    td:first-child {
      padding: ${token('space.025')} !important; // to match the design for left padding when row is expanded
    }
    // for adding dropdown on the left and adding a padding
    td.expandable-row-cell {
      display: flex;
      align-items: center;
      padding-left: ${token('space.400')} !important; // to match the design for left padding when row is expanded
    }
    :hover {
      .cloud-site-empty-cell {
        border-left: 1px solid var(--ds-surface-sunken) !important;
        background: ${token('elevation.surface.hovered')};
      }
    }
  }

  .needs-review-row {
    background-color: ${token('color.background.accent.yellow.subtlest')} !important;
  }

  .product-line-item-row {
    .first-cell-table {
      margin-left: ${token('space.150')};
    }
    td:first-child {
      padding: ${token('space.025')} !important; // to match the design for left padding when row is expanded
    }
    td.col-product-name {
      display: flex;
      align-items: center;
      padding-left: ${token('space.400')} !important; // to match the design for left padding when row is expanded
      min-height: 50px;
      height: auto !important;
    }

    .product-rows-container {
      &--product-wrapper {
        &--product-content {
          align-items: center;
          padding-left: ${token('space.150')};
        }
      }
    }

    .expandable-row-button {
      margin-left: ${token('space.150')};
    }
  }

  .app-line-item-row {
    .col-select-product {
      padding: ${token('space.025')} !important; // this is required to override the dynamic table table css
    }
    .product-rows-container {
      &--product-wrapper {
        &--product-content {
          align-items: center;
          padding-left: ${token('space.800')} !important;
        }
      }
      &--product-name {
        > div:first-child {
          text-overflow: ellipsis;
          max-width: 230px;
          overflow: hidden;
          white-space: nowrap;
        }
      }
    }
  }

  .expandable-row {
    border-top: 1px solid ${token('color.border.disabled')} !important; // to override the default border color
    :hover {
      background-color: var(
        --ds-surface-hovered
      ) !important; // to override the default hover behavior of none as it only highlights first td instead of whole row
    }
  }

  .no-expandable-row {
    .col-product-name {
      .product-rows-container {
        &--product-wrapper {
          &--product-content {
            padding-left: ${token('space.300')};
          }
        }
      }
    }
  }
`;
